import React from 'react';
import { useFreeTrialContext } from '@/contexts/FreeTrialContext';
import { useAuth } from '@/contexts/AuthContext';
import { router, AppRoute } from '@/lib/router';
import { Info, Zap, Clock, AlertTriangle, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/contexts/I18nContext';

interface FreeTrialNoticeBannerProps {
  compact?: boolean; // 紧凑模式，只显示 Token 和时间信息
}

export const FreeTrialNoticeBanner: React.FC<FreeTrialNoticeBannerProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { freeTrialStatus, hasTrialStatus, error } = useFreeTrialContext();

  // 如果已登录，不显示免费试用说明
  if (isAuthenticated) {
    return null;
  }

  // 如果没有试用状态且没有错误信息，不显示
  if (!hasTrialStatus && !error) {
    return null;
  }

  const formatExpiryTime = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();

    if (diff <= 0) {
      return t('freeTrial.expired');
    }

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}${t('freeTrial.hours')}${minutes % 60}${t('freeTrial.minutes')}`;
    } else {
      return `${minutes}${t('freeTrial.minutes')}`;
    }
  };

  // 判断试用状态
  const isTokensExhausted = freeTrialStatus?.remainingTokens === 0;
  const isExpired = freeTrialStatus?.isExpired || (freeTrialStatus?.expiresAt && new Date(freeTrialStatus.expiresAt) <= new Date());
  const isTrialEnded = isTokensExhausted || isExpired || !freeTrialStatus?.isActive;

  // 如果有错误信息但没有试用状态，显示错误banner
  if (error && !freeTrialStatus) {
    return (
      <div className="bg-red-50 border-b border-red-200">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          {/* 🔑 移动端紧凑布局 */}
          <div className="flex items-center justify-between gap-2 sm:hidden">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm font-medium text-red-800">{t('freeTrial.freeTrialNotAvailable')}</span>
            </div>
            <Button
              onClick={() => router.navigate(AppRoute.LOGIN)}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 h-7"
            >
              {t('auth.login')}
            </Button>
          </div>

          {/* 🔑 桌面端完整布局 */}
          <div className="hidden sm:flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-red-600" />
                <span className="font-semibold text-red-800">{t('freeTrial.freeTrialNotAvailable')}</span>
              </div>

              <div className="text-sm text-red-700 space-y-1">
                <div>• <strong>{t('freeTrial.errorMessage')}:</strong> {error}</div>
                {error.includes('expired') && (
                  <div>• <strong>{t('freeTrial.reason')}:</strong> {t('freeTrial.deviceUsedTrialExpired')}</div>
                )}
                {error.includes('exhausted') && (
                  <div>• <strong>{t('freeTrial.reason')}:</strong> {t('freeTrial.deviceUsedTrialExhausted')}</div>
                )}
                <div>• <strong>{t('freeTrial.solution')}:</strong> {t('freeTrial.registerLogin')}</div>
              </div>

              <div className="flex items-center gap-4 mt-3">
                <Button
                  onClick={() => router.navigate(AppRoute.LOGIN)}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('freeTrial.registerForMore')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 如果没有试用状态，不显示
  if (!freeTrialStatus) {
    return null;
  }

  // 根据试用状态显示不同的内容
  if (isTrialEnded) {
    // 试用结束状态
    return (
      <div className="bg-red-50 border-b border-red-200">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          {/* 🔑 移动端紧凑布局 */}
          <div className="flex items-center justify-between gap-2 sm:hidden">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm font-medium text-red-800">{t('freeTrial.trialEndedShort')}</span>
            </div>
            <Button
              onClick={() => router.navigate(AppRoute.LOGIN)}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 h-7"
            >
              {t('auth.login')}
            </Button>
          </div>

          {/* 🔑 桌面端完整布局 */}
          <div className="hidden sm:flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-red-600" />
                <span className="font-semibold text-red-800">{t('freeTrial.freeTrialEndedFull')}</span>
              </div>

              <div className="text-sm text-red-700 space-y-1">
                {isTokensExhausted && (
                  <div>• <strong>{t('freeTrial.tokensUsedUp')}</strong> - {t('freeTrial.tokensUsedUpDesc')}</div>
                )}
                {isExpired && (
                  <div>• <strong>{t('freeTrial.trialExpired')}</strong> - {t('freeTrial.trialExpiredDesc')}</div>
                )}
                <div>• <strong>{t('freeTrial.registerForMore')}</strong> - {t('freeTrial.registerForMoreDesc')}</div>
                <div>• <strong>{t('freeTrial.fullFeatureExperience')}</strong> - {t('freeTrial.fullFeatureExperienceDesc')}</div>
              </div>

              <div className="flex items-center gap-4 mt-3">
                <Button
                  onClick={() => router.navigate(AppRoute.LOGIN)}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <User className="w-4 h-4 mr-2" />
                  {t('freeTrial.registerForMore')}
                </Button>

                <div className="text-xs text-red-600">
                  {isTokensExhausted && <span>{t('freeTrial.remainingTokens')}: 0</span>}
                  {freeTrialStatus?.expiresAt && (
                    <span className="ml-3">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {isExpired ? t('freeTrial.expired') : `${t('freeTrial.remainingTime')}: ${formatExpiryTime(freeTrialStatus.expiresAt)}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 正常试用状态
  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
        {/* 🔑 紧凑模式 - 只显示 Token 和时间信息 */}
        {compact ? (
          <div className="flex items-center justify-center gap-4 text-xs text-amber-700">
            <div className="flex items-center gap-1">
              <span>{t('freeTrial.remainingTokens')}:</span>
              <span className="font-bold">{freeTrialStatus?.remainingTokens?.toLocaleString() || 0}</span>
            </div>

            {freeTrialStatus?.expiresAt && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{t('freeTrial.remainingTime')}: {formatExpiryTime(freeTrialStatus.expiresAt)}</span>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* 🔑 移动端紧凑布局 - 只显示状态和登录按钮 */}
            <div className="flex items-center justify-between gap-2 sm:hidden">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-sm font-medium text-amber-800">{t('freeTrial.freeTrialActive')}</span>
              </div>
              <Button
                onClick={() => router.navigate(AppRoute.LOGIN)}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1 h-7"
              >
                {t('auth.login')}
              </Button>
            </div>

            {/* 🔑 桌面端完整布局 */}
            <div className="hidden sm:flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Info className="w-5 h-5 text-amber-600" />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-amber-600" />
                  <span className="font-semibold text-amber-800">{t('freeTrial.freeTrialNotice')}</span>
                </div>

                <div className="text-sm text-amber-700 space-y-1">
                  <div>• <strong>{t('freeTrial.conversationsNotSaved')}</strong> - {t('freeTrial.conversationsNotSavedDesc')}</div>
                  <div>• <strong>{t('freeTrial.experienceOnly')}</strong> - {t('freeTrial.experienceOnlyDesc')}</div>
                  <div>• <strong>{t('freeTrial.loginForPermanentSave')}</strong> - {t('freeTrial.loginForPermanentSaveDesc')}</div>
                </div>

                <div className="flex items-center gap-4 mt-2 text-xs text-amber-600">
                  <div className="flex items-center gap-1">
                    <span>{t('freeTrial.remainingTokens')}:</span>
                    <span className="font-bold">{freeTrialStatus?.remainingTokens?.toLocaleString() || 0}</span>
                  </div>

                  {freeTrialStatus?.expiresAt && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{t('freeTrial.remainingTime')}: {formatExpiryTime(freeTrialStatus.expiresAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}; 